/**
 * Sign-in — ADR-001 §6.1. Google OAuth 2.0 (code + PKCE) → server-side session cookie `ops_sid`
 * (HttpOnly; Secure; SameSite=Lax; Path=/; 12 h absolute, 8 h idle, sliding).
 *
 * A valid amplispot.com Google account is necessary but not sufficient: the user must also exist in
 * `users` with status `active`. Sign-in never creates access.
 */
import { z } from 'zod';
/** Only same-site relative paths are accepted, so `redirect` cannot be used as an open redirect. */
export const SafeRedirect = z
    .string()
    .max(512)
    .regex(/^\/(?!\/)[^\s\\]*$/, 'redirect must be a path on this site, starting with a single /');
export const GoogleStartQuery = z.object({
    /** Where to land after a successful sign-in. Default: the role's `home_route`. */
    redirect: SafeRedirect.optional(),
});
/** Google sends either `code`+`state`, or `error`+`state` when the user cancels. */
export const GoogleCallbackQuery = z.object({
    code: z.string().min(1).max(2048).optional(),
    state: z.string().min(1).max(512),
    error: z.string().max(256).optional(),
    scope: z.string().max(2048).optional(),
    hd: z.string().max(256).optional(),
    authuser: z.string().max(16).optional(),
    prompt: z.string().max(64).optional(),
});
/**
 * The callback is a browser navigation, so failures are sent as `302` to `/sign-in?error=<SignInError>`
 * (the SPA's sign-in screen prints the matching message). For `not_provisioned`, a request that sends
 * `Accept: application/json` instead receives `403` with the standard `ErrorBody`.
 */
export const SignInError = z
    .enum([
    'not_provisioned', // valid Google account on the domain, but no active user row
    'domain_not_allowed', // hd claim or email domain is not the allowed sign-in domain
    'google_cancelled', // the user backed out at Google
    'state_mismatch', // state/PKCE verifier missing or expired — start again
    'google_unavailable', // token exchange or JWKS fetch failed
])
    .meta({ id: 'SignInError' });
/** Copy for each sign-in error (guide §9: say what went wrong and how to fix it). */
export const SIGN_IN_ERROR_MESSAGE = {
    not_provisioned: 'Your Google account is not set up in Amplispot Ops yet. Ask the founder to add you in Settings.',
    domain_not_allowed: 'Sign in with your amplispot.com work account.',
    google_cancelled: 'Sign-in was cancelled. Press Sign in with Google to try again.',
    state_mismatch: 'That sign-in link expired. Press Sign in with Google to start again.',
    google_unavailable: 'Google did not respond. Wait a minute and try again.',
};
export const SignInErrorQuery = z.object({ error: SignInError.optional() });
/**
 * NON-PRODUCTION ONLY. `POST /auth/dev/sign-in` lets local runs and the role-matrix tests sign in as a
 * provisioned user without Google. The route is not registered when `NODE_ENV=production`, and in other
 * environments only when `AUTH_DEV_LOGIN=1`. It applies the same `active` user check as Google sign-in.
 */
export const DevSignInBody = z
    .object({
    email: z.email().transform((s) => s.trim().toLowerCase()),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------
// Email + password — the second sign-in method beside Google (operator 17 Oct 2026: production must be testable
// without a Google OAuth client). Same session cookie, same active-user check; works in production.
// ---------------------------------------------------------------------------------------------------------------
/** A password is 10–256 characters (any characters; the length cap bounds the hashing work). */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 256;
/** Failed password sign-ins for one email (known or not) before it is locked, and for how long. */
export const PASSWORD_LOCKOUT_FAILURES = 10;
export const PASSWORD_LOCKOUT_MINUTES = 15;
export const NewPassword = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters.`);
/**
 * `POST /auth/password/sign-in`. The password is checked as typed (never trimmed); the email is trimmed and
 * lower-cased. A wrong password, an unknown email and an account with no password all get the same 401.
 */
export const PasswordSignInBody = z
    .object({
    // Trim and lower-case BEFORE validating, so a pasted " Name@Example.com " signs in. A preprocess (not
    // string().trim().pipe(email())) so OpenAPI still documents the field as an email with its max length.
    email: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v), z.email().max(320)),
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
})
    .strict()
    .meta({ id: 'PasswordSignInBody' });
/** `POST /auth/password/change`: the current password (the temporary one after a reset) and the new one. */
export const PasswordChangeBody = z
    .object({
    current_password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    new_password: NewPassword,
})
    .strict()
    .meta({ id: 'PasswordChangeBody' });
/** The one message for every failed password sign-in (it never says whether the email exists). */
export const INVALID_CREDENTIALS_MESSAGE = 'That email and password do not match. Check both and try again, or ask a manager to reset your password.';
//# sourceMappingURL=auth.js.map